import {
	ExamItemType,
	ExamStatusEnum,
} from 'domain/usecases/private/my-hospitalization/get-exams-list';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {useDebounceSearch} from 'presentation/hooks/useDebounceSearch';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {useGetPaginatedItemsReactQuery} from 'presentation/pages/private/hooks/useGetPaginatedItemsReactQuery';
import {useMemo} from 'react';
import {textHandle} from 'presentation/utils/textHandle';
import {translations} from 'presentation/translations-new';
import {getExamsList} from '../services/getExamsList';

const {
	filter: {image, laboratory},
} = translations['pt-br'].privateFlow.myHospitalization.exams;

const SIZE = 6;

export const useRequestExams = () => {
	const {
		SEARCH_PARAMS: {category},
		setSearchParams,
	} = useQueryParams();

	const {searchValueState, debouncedQueryFunc, clearSearchParams, abortDebounce} =
		useDebounceSearch({});

	const handleClear = (type: 'all' | 'search' | 'filter') => {
		if (['all', 'search'].includes(type)) {
			clearSearchParams();
		}
		if (['all', 'filter'].includes(type)) {
			setSearchParams(prev => {
				prev.delete('category');
				return prev;
			});
		}
	};

	const {
		items,
		loadingItemsList,
		itemsListIsEmpty,
		itemsListHasError,
		loadMore,
		refresh,
		isLoading,
	} = useGetPaginatedItemsReactQuery<ExamItemType>({
		queryKey: [QUERY_KEYS.HOSPITALIZATION_EXAMS_LIST, searchValueState],
		queryFn: ({page, signal}) =>
			getExamsList({
				'exam-description': searchValueState.toUpperCase(),
				page,
				size: SIZE,
				signal,
			}),
	});

	const formattedData = useMemo(
		() =>
			items.map(exam => ({
				examDate: exam.requestDate,
				examTime: exam.requestTime,
				examType: exam.examType === ExamStatusEnum.IMAGE ? image : laboratory,
				examName: textHandle(exam.examDescription).capitalize(),
				doctorName: textHandle(exam.requestingDoctorName).capitalize(),
			})) ?? [],
		[items],
	);

	const filteredData = useMemo(
		() =>
			formattedData.filter(exam =>
				category ? category.toLowerCase() === exam.examType.toLowerCase() : true,
			),
		[formattedData, category],
	);

	const isEmptyFiltered = filteredData?.length === 0;

	return {
		items: filteredData,
		itemsListIsEmpty,
		filteredData,
		handleClear,
		loadMore,
		isEmptyFiltered,
		itemsListHasError,
		isLoading,
		loadingItemsList,
		refresh,
		searchValueState,
		debouncedQueryFunc,
		abortDebounce,
	};
};
